import {
  Voucher,
  VoucherFormData,
  VoucherFilters,
  VoucherStatus,
  VoucherType,
} from '../types/voucher.types'

// Mock data for demonstration
const mockVouchers: Voucher[] = [
  {
    id: '1',
    voucherNo: 'CRV-PB-HQ-2024-001',
    voucherType: VoucherType.CRV,
    date: '2024-01-15',
    transactionMode: 'Cash' as any,
    amount: 50000,
    totalDebit: 50000,
    totalCredit: 50000,
    partyName: 'ABC Corporation',
    status: VoucherStatus.APPROVED,
    isEditable: false,
    isLocked: false,
    fiscalYear: '2024',
    lines: [
      {
        id: '1',
        accountId: '1',
        accountCode: '1100',
        accountName: 'Cash',
        debit: 50000,
        credit: 0,
      },
      {
        id: '2',
        accountId: '2',
        accountCode: '4000',
        accountName: 'Sales Revenue',
        debit: 0,
        credit: 50000,
      },
    ],
    approvalHistory: [],
    createdBy: 'John Doe',
    createdAt: '2024-01-15T10:00:00Z',
  },
]

class VoucherService {
  private vouchers: Voucher[] = mockVouchers

  async getVouchers(filters?: VoucherFilters): Promise<Voucher[]> {
    let filtered = [...this.vouchers]

    if (filters) {
      if (filters.voucherNo) {
        filtered = filtered.filter((v) =>
          v.voucherNo.toLowerCase().includes(filters.voucherNo!.toLowerCase()),
        )
      }
      if (filters.voucherType) {
        filtered = filtered.filter((v) => v.voucherType === filters.voucherType)
      }
      if (filters.status) {
        filtered = filtered.filter((v) => v.status === filters.status)
      }
      if (filters.dateFrom) {
        filtered = filtered.filter((v) => v.date >= filters.dateFrom!)
      }
      if (filters.dateTo) {
        filtered = filtered.filter((v) => v.date <= filters.dateTo!)
      }
    }

    return filtered
  }

  async getVoucherById(id: string): Promise<Voucher | null> {
    return this.vouchers.find((v) => v.id === id) || null
  }

  async createVoucher(data: VoucherFormData): Promise<Voucher> {
    const voucherNo = await this.generateVoucherNumber(data.voucherType)

    const newVoucher: Voucher = {
      id: Date.now().toString(),
      voucherNo,
      voucherType: data.voucherType,
      date: data.date,
      transactionMode: data.transactionMode,
      referenceNo: data.referenceNo,
      partyId: data.partyId,
      branchId: data.branchId,
      projectId: data.projectId,
      costCenterId: data.costCenterId,
      chequeDetails: data.chequeDetails,
      lines: data.lines,
      description: data.description,
      narration: data.narration,
      totalDebit: data.lines.reduce((sum, line) => sum + line.debit, 0),
      totalCredit: data.lines.reduce((sum, line) => sum + line.credit, 0),
      amount: data.lines.reduce((sum, line) => sum + line.debit, 0),
      status: VoucherStatus.DRAFT,
      isEditable: true,
      isLocked: false,
      fiscalYear: new Date(data.date).getFullYear().toString(),
      approvalHistory: [],
      createdBy: 'Current User',
      createdAt: new Date().toISOString(),
    }

    this.vouchers.push(newVoucher)
    return newVoucher
  }

  async updateVoucher(
    id: string,
    data: Partial<VoucherFormData>,
  ): Promise<Voucher> {
    const index = this.vouchers.findIndex((v) => v.id === id)
    if (index === -1) throw new Error('Voucher not found')

    const voucher = this.vouchers[index]
    if (!voucher.isEditable) {
      throw new Error('Voucher cannot be edited in current status')
    }

    this.vouchers[index] = {
      ...voucher,
      ...data,
      updatedBy: 'Current User',
      updatedAt: new Date().toISOString(),
    }

    return this.vouchers[index]
  }

  async completeVoucher(id: string): Promise<Voucher> {
    const voucher = await this.getVoucherById(id)
    if (!voucher) throw new Error('Voucher not found')

    voucher.status = VoucherStatus.COMPLETED
    voucher.isEditable = false
    voucher.completedBy = 'Current User'
    voucher.completedAt = new Date().toISOString()

    return voucher
  }

  async approveVoucher(id: string, comments?: string): Promise<Voucher> {
    const voucher = await this.getVoucherById(id)
    if (!voucher) throw new Error('Voucher not found')

    voucher.status = VoucherStatus.APPROVED
    voucher.approvedBy = 'Current User'
    voucher.approvedAt = new Date().toISOString()

    return voucher
  }

  async rejectVoucher(id: string, reason: string): Promise<Voucher> {
    const voucher = await this.getVoucherById(id)
    if (!voucher) throw new Error('Voucher not found')

    voucher.status = VoucherStatus.REJECTED
    voucher.isEditable = true

    return voucher
  }

  async deleteVoucher(id: string): Promise<void> {
    const voucher = await this.getVoucherById(id)
    if (!voucher) throw new Error('Voucher not found')

    if (voucher.status !== VoucherStatus.DRAFT) {
      throw new Error('Only draft vouchers can be deleted')
    }

    this.vouchers = this.vouchers.filter((v) => v.id !== id)
  }

  async generateVoucherNumber(type: VoucherType): Promise<string> {
    // Format: TYPE-BANK-BRANCH-YEAR-SERIAL
    const prefix = type
    const bankPrefix = 'PB' // Premier Bank
    const branchCode = 'HQ' // Headquarters
    const year = new Date().getFullYear()
    const serial = String(this.vouchers.length + 1).padStart(3, '0')

    return `${prefix}-${bankPrefix}-${branchCode}-${year}-${serial}`
  }

  validateBalance(lines: any[]): boolean {
    const totalDebit = lines.reduce((sum, line) => sum + (line.debit || 0), 0)
    const totalCredit = lines.reduce((sum, line) => sum + (line.credit || 0), 0)
    return Math.abs(totalDebit - totalCredit) < 0.01 // Allow for floating point precision
  }
}

export const voucherService = new VoucherService()
